/**
 * Client-side virtual try-on detection engine.
 *
 * Runs entirely in the browser. MediaPipe FaceLandmarker (Tasks API, VIDEO mode)
 * tracks the face every frame and returns landmarks + the head-pose matrix, which
 * the WebGL AR engine (arEngine.ts) uses to lock a glasses photo onto the face.
 *
 * Both the WASM runtime (/mediapipe-wasm) and the face model (.task) are
 * self-hosted from the app's own origin — no Google/CDN dependency.
 */

import type { FaceLandmarker } from '@mediapipe/tasks-vision';

// const MEDIAPIPE_WASM_PATH = '/mediapipe-wasm';

const MEDIAPIPE_WASM_PATH = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm';
const FACE_MODEL_URL = '/models/face_landmarker.task';

export interface NormLandmark { x: number; y: number; z: number }

/** Raw detection used by the 3D AR engine: landmarks + the head pose matrix. */
export interface RawDetection {
  landmarks: NormLandmark[];
  /** Column-major 4×4 facial transformation matrix (head pose), if available. */
  matrix: number[] | null;
}

let landmarkerPromise: Promise<FaceLandmarker> | null = null;

// detectForVideo (VIDEO mode) requires strictly increasing timestamps; a repeated
// or out-of-order value makes MediaPipe throw. Track the last accepted timestamp
// so we can skip stale frames instead of crashing the render loop.
let lastTimestampMs = -1;

/** Lazily create a single shared FaceLandmarker in VIDEO mode. */
async function getLandmarker(): Promise<FaceLandmarker> {
  if (!landmarkerPromise) {
    landmarkerPromise = (async () => {
      const vision = await import('@mediapipe/tasks-vision');
      const fileset = await vision.FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
      return vision.FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: FACE_MODEL_URL },
        runningMode: 'VIDEO',
        numFaces: 1,
        outputFacialTransformationMatrixes: true,
      });
    })();
  }
  return landmarkerPromise;
}

/** Warm up the model so the first live frame isn't janky. */
export async function preloadEngine(): Promise<void> {
  await getLandmarker();
}

/**
 * Raw landmark + head-pose detection for the 3D AR engine.
 * Returns null if no face is found this frame.
 */
export async function detectRaw(
  video: HTMLVideoElement,
  timestampMs: number
): Promise<RawDetection | null> {
  // Skip frames that aren't safe to detect on: video not decoded yet (zero
  // dimensions) or a timestamp that isn't strictly newer than the last one.
  // Calling detectForVideo in either case makes MediaPipe throw.
  if (!video.videoWidth || !video.videoHeight) return null;
  if (timestampMs <= lastTimestampMs) return null;
  lastTimestampMs = timestampMs;

  const landmarker = await getLandmarker();
  let result;
  try {
    result = landmarker.detectForVideo(video, timestampMs);
  } catch (err) {
    // Transient detection failure (e.g. video momentarily not ready) — drop this
    // frame rather than letting the error bubble up and kill the render loop.
    console.error('Error processing the video: ', err);
    return null;
  }
  if (!result.faceLandmarks || result.faceLandmarks.length === 0) return null;
  return {
    landmarks: result.faceLandmarks[0],
    matrix: result.facialTransformationMatrixes?.[0]?.data ?? null,
  };
}
